<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Spray Records</h1>
    <p class="text-muted mb-0">Track and manage your aerial spray applications</p>
  </div>
  <button class="btn btn-primary" (click)="addSprayRecord()">
    <i class="bi bi-plus-lg me-2"></i>Add Record
  </button>
</div>

<!-- Stats Summary -->
<div class="row g-3 mb-4">
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-primary-subtle">
            <i class="bi bi-journal-check text-primary"></i>
          </div>
          <div>
            <div class="stat-value">{{ completedRecords().length }}</div>
            <div class="stat-label text-muted">Completed Records</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-warning-subtle">
            <i class="bi bi-clock text-warning"></i>
          </div>
          <div>
            <div class="stat-value">{{ pendingRecords().length }}</div>
            <div class="stat-label text-muted">Pending Applications</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-success-subtle">
            <i class="bi bi-map text-success"></i>
          </div>
          <div>
            <div class="stat-value">{{ totalAcreage() | number:'1.1-1' }}</div>
            <div class="stat-label text-muted">Total Acres Sprayed</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Records List -->
<div class="card">
  <div class="card-header p-3">
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="mb-0">Recent Spray Records</h5>
      <div class="d-flex gap-2">
        <button
          class="btn btn-sm"
          [class.btn-primary]="isFilterOpen() || activeFilterCount() > 0"
          [class.btn-outline-secondary]="!isFilterOpen() && activeFilterCount() === 0"
          (click)="toggleFilter()"
        >
          <i class="bi bi-funnel me-1"></i>Filter
          <span *ngIf="activeFilterCount() > 0" class="badge bg-white text-primary ms-1">{{ activeFilterCount() }}</span>
        </button>
        <button class="btn btn-sm btn-outline-secondary" (click)="openExportModal()">
          <i class="bi bi-download me-1"></i>Export
        </button>
      </div>
    </div>

    <!-- Filter Panel (Dropdown) -->
    <div class="filter-panel" *ngIf="isFilterOpen()">
      <hr class="my-3">
      <div class="row g-3">
        <!-- Status Filter -->
        <div class="col-md-3">
          <label class="form-label small">Status</label>
          <select class="form-select form-select-sm" [ngModel]="filters.status" (ngModelChange)="updateFilter('status', $event)">
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <!-- Field Filter -->
        <div class="col-md-3">
          <label class="form-label small">Field</label>
          <select class="form-select form-select-sm" [ngModel]="filters.field" (ngModelChange)="updateFilter('field', $event)">
            <option value="">All Fields</option>
            <option *ngFor="let field of uniqueFields()" [value]="field">{{ field }}</option>
          </select>
        </div>

        <!-- Pilot Filter -->
        <div class="col-md-3">
          <label class="form-label small">Pilot</label>
          <select class="form-select form-select-sm" [ngModel]="filters.pilot" (ngModelChange)="updateFilter('pilot', $event)">
            <option value="">All Pilots</option>
            <option *ngFor="let pilot of uniquePilots()" [value]="pilot">{{ pilot }}</option>
          </select>
        </div>

        <!-- Date Range -->
        <div class="col-md-3">
          <label class="form-label small">Date Range</label>
          <div class="d-flex gap-2">
            <input
              type="date"
              class="form-control form-control-sm"
              [ngModel]="filters.dateFrom"
              (ngModelChange)="updateFilter('dateFrom', $event)"
              placeholder="From"
            >
            <input
              type="date"
              class="form-control form-control-sm"
              [ngModel]="filters.dateTo"
              (ngModelChange)="updateFilter('dateTo', $event)"
              placeholder="To"
            >
          </div>
        </div>
      </div>

      <!-- Clear Filters Button -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <span class="text-muted small">
          Showing {{ filteredRecords().length }} of {{ sprayRecords().length }} records
        </span>
        <button
          *ngIf="activeFilterCount() > 0"
          class="btn btn-sm btn-link text-decoration-none"
          (click)="clearFilters()"
        >
          <i class="bi bi-x-lg me-1"></i>Clear Filters
        </button>
      </div>
    </div>
  </div>

  <div class="card-body p-0">
    <ng-container *ngIf="!isLoading() && filteredRecords().length > 0; else emptyState">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th class="ps-3">Date</th>
              <th>Field / Crop</th>
              <th>Customer</th>
              <th>Chemical</th>
              <th>Acreage</th>
              <th>Flights</th>
              <th>Pilot</th>
              <th>Status</th>
              <th class="text-end pe-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let record of filteredRecords()" class="record-row">
              <td class="ps-3">
                <span class="fw-medium">{{ record.date | adnDate }}</span>
              </td>
              <td>
                <div class="d-flex flex-column">
                  <span class="fw-medium">{{ record.fieldName }}</span>
                  <span class="text-muted small">{{ record.cropType }}</span>
                </div>
              </td>
              <td>
                <a *ngIf="record.customerName && record.customerId"
                   [routerLink]="['/customers', record.customerId, 'view']"
                   class="text-decoration-none">
                  {{ record.customerName }}
                </a>
                <span *ngIf="!record.customerName" class="text-muted">—</span>
              </td>
              <td>
                <div class="d-flex flex-column">
                  <span>{{ record.chemicalName }}</span>
                  <span class="text-muted small">{{ record.applicationRate }}</span>
                </div>
              </td>
              <td>{{ record.acreage | number:'1.1-1' }} ac</td>
              <!-- FAA §F.2.d roll-up. `!= null` so a record with no aircraft
                   shows "—" rather than 0, and the passive badge marks rows
                   whose count will be filed as 1. Badge only - no action, the
                   fix is to open the record and enter the number. -->
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span *ngIf="record.filedFlights != null">{{ record.filedFlights }}</span>
                  <span *ngIf="record.filedFlights == null" class="text-muted">&mdash;</span>
                  <span *ngIf="record.needsFlightCount"
                        class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
                        title="At least one aircraft has no flight count entered and will be filed as 1">
                    Needs count
                  </span>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column">
                  <span>{{ record.pilotName }}</span>
                  <span class="text-muted small">{{ record.droneId }}</span>
                </div>
              </td>
              <td>
                <span class="badge" [ngClass]="record.status | statusBadge">
                  {{ getStatusText(record.status) }}
                </span>
              </td>
              <td class="text-end pe-3">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="viewRecord(record)" title="View details">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button class="btn btn-outline-secondary" (click)="editRecord(record)" title="Edit record">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-outline-secondary text-danger" (click)="deleteRecord(record)" title="Delete record">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <ng-container *ngIf="sprayRecords().length === 0; else noResults">
          <div class="empty-state-icon mb-3">
            <i class="bi bi-droplet-half"></i>
          </div>
          <h5>No Spray Records Yet</h5>
          <p class="text-muted mb-3">Start tracking your aerial spray applications by adding your first record.</p>
          <button class="btn btn-primary" (click)="addSprayRecord()">
            <i class="bi bi-plus-lg me-2"></i>Add First Record
          </button>
        </ng-container>
        <ng-template #noResults>
          <div class="empty-state-icon mb-3">
            <i class="bi bi-funnel"></i>
          </div>
          <h5>No Records Match Filters</h5>
          <p class="text-muted mb-3">Try adjusting your filters to see more results.</p>
          <button class="btn btn-outline-primary" (click)="clearFilters()">
            <i class="bi bi-x-lg me-2"></i>Clear Filters
          </button>
        </ng-template>
      </div>
    </ng-template>
  </div>
</div>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>

<!-- Export Modal -->
<app-export-modal
  #exportModal
  (exported)="onExport($event)"
></app-export-modal>
